import * as XLSX from 'xlsx';

/* ── Sheet definitions ───────────────────────────────────────────────────── */

export const SHEET_PROVINCE = 'Provinces';
export const SHEET_DISTRICT = 'Districts';
export const SHEET_ELECTORATE = 'Electorates';
export const SHEET_STATION = 'Police Stations';

export const REGION_SHEETS = [
  {
    key: 'provinces',
    sheet: SHEET_PROVINCE,
    label: 'Province',
    headers: ['Province Name'],
    fields: ['name'],
  },
  {
    key: 'districts',
    sheet: SHEET_DISTRICT,
    label: 'District',
    headers: ['District Name', 'Province Name'],
    fields: ['name', 'province'],
  },
  {
    key: 'electorates',
    sheet: SHEET_ELECTORATE,
    label: 'Electorate',
    headers: ['Electorate Area', 'Province Name', 'District Name'],
    fields: ['name', 'province', 'district'],
  },
  {
    key: 'stations',
    sheet: SHEET_STATION,
    label: 'Police Station',
    headers: ['Station Name', 'Province Name', 'District Name', 'Electorate Area'],
    fields: ['name', 'province', 'district', 'electorate'],
  },
];

/* ── Name matching ───────────────────────────────────────────────────────── */

const fmt = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** Case- and spacing-insensitive. "  western   province " === "Western Province". */
const norm = (v) => fmt(v).toUpperCase().replace(/\s+/g, ' ');


const normProvince = (v) => norm(v).replace(/\s*PROVINCE$/, '');
const normDistrict = (v) => norm(v).replace(/\s*DISTRICT$/, '');

/* ── Reading the master lists that already exist ─────────────────────────── */

const pick = (row, keys, pattern) => {
  if (!row || typeof row !== 'object') return '';
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && fmt(row[k]) !== '') return fmt(row[k]);
  }
  const rowKeys = Object.keys(row);
  for (const target of keys) {
    const hit = rowKeys.find((k) => k.toLowerCase() === target.toLowerCase());
    if (hit && fmt(row[hit]) !== '') return fmt(row[hit]);
  }
  if (pattern) {
    const hit = rowKeys.find((k) => pattern.test(k));
    if (hit && fmt(row[hit]) !== '') return fmt(row[hit]);
  }
  return '';
};

/** Unwraps `{ data: { ResultSet: [...] } }`, a bare array, or anything between. */
export const rowsOf = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const body = res.data !== undefined && res.data !== null ? res.data : res;
  const set = body.ResultSet ?? body.resultSet ?? body.Result ?? body.Data ?? null;
  return Array.isArray(set) ? set : [];
};

export const readProvinceRows = (raw) => rowsOf(raw).map((p) => ({
  code: pick(p, ['PROVINCE_CODE', 'ProvinceCode', 'provinceCode', 'HPD_PROVINCE_CODE', 'Code', 'code', 'Id', 'id'], /province.*code|^code$|^id$/i),
  name: pick(p, ['PROVINCE_NAME', 'ProvinceName', 'provinceName', 'HPD_PROVINCE_NAME', 'Name', 'name', 'Description'], /province.*name|^name$|desc/i),
})).filter((p) => p.code);

export const readDistrictRows = (raw) => rowsOf(raw).map((d) => ({
  code: pick(d, ['DISTRICT_CODE', 'DistrictCode', 'districtCode', 'HDD_DISTRICT_CODE', 'Code', 'code', 'Id', 'id'], /district.*code|^code$|^id$/i),
  name: pick(d, ['DISTRICT_NAME', 'DistrictName', 'districtName', 'HDD_DISTRICT_NAME', 'Name', 'name', 'Description'], /district.*name|^name$|desc/i),
  provinceCode: pick(d, ['DISTRICT_PROVINCE_CODE', 'districtProvinceCode', 'PROVINCE_CODE', 'ProvinceCode', 'provinceCode'], /province.*code/i),
})).filter((d) => d.code);

export const readElectorateRows = (raw) => rowsOf(raw).map((e) => ({
  code: pick(e, ['ELECTORATE_CODE', 'ElectorateCode', 'electorateCode'], /electorate.*code/i),
  name: pick(e, ['ELECTORATE_AREA', 'ElectorateArea', 'electorateArea'], /electorate.*area|^area$/i),
  provinceCode: pick(e, ['ELECTORATE_PROVINCE', 'ElectorateProvince', 'electorateProvince'], /electorate.*province/i),
  districtCode: pick(e, ['ELECTORATE_DISTRICT', 'ElectorateDistrict', 'electorateDistrict'], /electorate.*district/i),
})).filter((e) => e.code);

export const readStationRows = (raw) => rowsOf(raw).map((s) => ({
  code: pick(s, ['HPD_STATION_CODE', 'StationCode', 'stationCode'], /station.*code/i),
  name: pick(s, ['HPD_POLICESTATION_NAME', 'PoliceStationName', 'policeStationName', 'Name', 'name'], /policestation.*name|station.*name|^name$/i),
  electorateCode: pick(s, ['HPD_ELECTORATE_CODE', 'ElectorateCode', 'electorateCode'], /electorate.*code/i),
})).filter((s) => s.code);

/* ── The template ────────────────────────────────────────────────────────── */


const SEED_PROVINCES = [
  'Western Province', 'Central Province', 'Southern Province', 'Northern Province',
  'Eastern Province', 'North Western Province', 'North Central Province',
  'Uva Province', 'Sabaragamuwa Province',
];

const SEED_DISTRICTS = [
  ['Colombo', 'Western Province'], ['Gampaha', 'Western Province'], ['Kalutara', 'Western Province'],
  ['Kandy', 'Central Province'], ['Matale', 'Central Province'], ['Nuwara Eliya', 'Central Province'],
  ['Galle', 'Southern Province'], ['Matara', 'Southern Province'], ['Hambantota', 'Southern Province'],
  ['Jaffna', 'Northern Province'], ['Kilinochchi', 'Northern Province'], ['Mannar', 'Northern Province'],
  ['Vavuniya', 'Northern Province'], ['Mullaitivu', 'Northern Province'],
  ['Batticaloa', 'Eastern Province'], ['Ampara', 'Eastern Province'], ['Trincomalee', 'Eastern Province'],
  ['Kurunegala', 'North Western Province'], ['Puttalam', 'North Western Province'],
  ['Anuradhapura', 'North Central Province'], ['Polonnaruwa', 'North Central Province'],
  ['Badulla', 'Uva Province'], ['Monaragala', 'Uva Province'],
  ['Ratnapura', 'Sabaragamuwa Province'], ['Kegalle', 'Sabaragamuwa Province'],
];


const SAMPLE_ELECTORATES = [
  ['Kaduwela', 'Western Province', 'Colombo'],
  ['Kolonnawa', 'Western Province', 'Colombo'],
  ['Balangoda', 'Sabaragamuwa Province', 'Ratnapura'],
];

const SAMPLE_STATIONS = [
  ['Kaduwela Police Station', 'Western Province', 'Colombo', 'Kaduwela'],
  ['Welikada Police Station', 'Western Province', 'Colombo', 'Kolonnawa'],
  ['Balangoda Police Station', 'Sabaragamuwa Province', 'Ratnapura', 'Balangoda'],
];

/* The Instructions sheet is written per download, because what the workbook
   already contains depends on what the server already has. */
const instructionRows = (counts, live) => ([
  ['Region Master Import'],
  ['Province · District · Electorate · Police Station — one workbook, imported top to bottom.'],
  [],
  ['How it works'],
  ['1', 'The four sheets are imported in order: Provinces, then Districts, then Electorates, then Police Stations.'],
  ['2', 'You never type a code. The system allocates every code on insert, so each sheet names its parent instead.'],
  ['3', 'Parent names must match exactly — a district whose Province Name is not on the Provinces sheet (or already on the server) is rejected.'],
  ['4', 'A row whose name already exists under the same parent is skipped, not duplicated. Re-uploading the same file is safe.'],
  ['5', 'The whole workbook is checked before anything is written. If a row is wrong, nothing is imported until you fix it.'],
  [],
  [live ? 'What is already in the system' : 'What is already filled in'],
  [live
    ? 'Every sheet below was filled from the live master lists, so the names are spelled exactly as the system stores them. Add your new rows underneath; the existing ones are recognised and skipped.'
    : 'The lists could not be read from the server, so the sheets below carry the standard Sri Lankan provinces and districts plus example rows. Check them against the system before importing.'],
  ['Provinces', `${counts.provinces} row(s)`],
  ['Districts', `${counts.districts} row(s)`],
  ['Electorates', `${counts.electorates} row(s)`],
  ['Police Stations', `${counts.stations} row(s)`],
  [],
  ['Notes'],
  ['·', 'Do not rename the sheets or change the header row — both are matched by name.'],
  ['·', 'Blank rows are ignored, so you can leave gaps while working.'],
  ['·', 'You can import one level at a time: fill only the sheet you are working on and leave the rest empty.'],
  ['·', 'The Reference Lists sheet is read-only — copy parent names from it if you are unsure of the spelling.'],
]);

/**
 * Builds the workbook from the master lists that are actually on the server, so
 * the sheets show real data rather than a guess — the same idea as the employee
 * template's Reference Lists sheet.
 *
 * @param {Object} existing  { provinces, districts, electorates, stations } as
 *   returned by readProvinceRows and friends. Anything missing falls back to the
 *   standard Sri Lankan seed data, so the button still works offline.
 */
export function buildRegionTemplateWorkbook(existing = {}) {
  const provinces   = Array.isArray(existing.provinces) ? existing.provinces : [];
  const districts   = Array.isArray(existing.districts) ? existing.districts : [];
  const electorates = Array.isArray(existing.electorates) ? existing.electorates : [];
  const stations    = Array.isArray(existing.stations) ? existing.stations : [];

  const live = provinces.length > 0;

  /* Code → name, so each child row can name its parent in words. */
  const provName = new Map(provinces.map((p) => [String(p.code), p.name]));
  const distName = new Map(districts.map((d) => [String(d.code), d.name]));
  const distProv = new Map(districts.map((d) => [String(d.code), String(d.provinceCode)]));
  const elecById = new Map(electorates.map((e) => [String(e.code), e]));

  const provinceRows = live
    ? provinces.map((p) => [p.name])
    : SEED_PROVINCES.map((p) => [p]);

  const districtRows = districts.length
    ? districts.map((d) => [d.name, provName.get(String(d.provinceCode)) || ''])
    : (live ? [] : SEED_DISTRICTS);

  const electorateRows = electorates.length
    ? electorates.map((e) => [
        e.name,
        provName.get(String(e.provinceCode)) || '',
        distName.get(String(e.districtCode)) || '',
      ])
    : (live ? [] : SAMPLE_ELECTORATES);

  const stationRows = stations.length
    ? stations.map((s) => {
        const e = elecById.get(String(s.electorateCode)) || {};
        return [
          s.name,
          provName.get(String(e.provinceCode)) || '',
          distName.get(String(e.districtCode)) || '',
          e.name || '',
        ];
      })
    : (live ? [] : SAMPLE_STATIONS);

  const counts = {
    provinces: provinceRows.length,
    districts: districtRows.length,
    electorates: electorateRows.length,
    stations: stationRows.length,
  };

  const wb = XLSX.utils.book_new();

  const wsInfo = XLSX.utils.aoa_to_sheet(instructionRows(counts, live));
  wsInfo['!cols'] = [{ wch: 18 }, { wch: 104 }];
  XLSX.utils.book_append_sheet(wb, wsInfo, 'Instructions');

  const sheet = (headers, rows, widths) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = widths.map((wch) => ({ wch }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 }, e: { r: Math.max(rows.length, 1), c: headers.length - 1 },
    }) };
    return ws;
  };

  XLSX.utils.book_append_sheet(wb,
    sheet(['Province Name'], provinceRows, [30]), SHEET_PROVINCE);
  XLSX.utils.book_append_sheet(wb,
    sheet(['District Name', 'Province Name'], districtRows, [26, 30]), SHEET_DISTRICT);
  XLSX.utils.book_append_sheet(wb,
    sheet(['Electorate Area', 'Province Name', 'District Name'], electorateRows, [28, 30, 26]), SHEET_ELECTORATE);
  XLSX.utils.book_append_sheet(wb,
    sheet(['Station Name', 'Province Name', 'District Name', 'Electorate Area'], stationRows, [34, 30, 26, 28]), SHEET_STATION);

  /* Reference Lists — the existing names on their own, side by side, to copy
     parent spellings from. Mirrors the employee import template. */
  const refCols = [
    ['Provinces', provinceRows.map((r) => r[0])],
    ['Districts', districtRows.map((r) => (r[1] ? `${r[0]} (${r[1]})` : r[0]))],
    ['Electorates', electorateRows.map((r) => (r[2] ? `${r[0]} (${r[2]})` : r[0]))],
    ['Police Stations', stationRows.map((r) => (r[3] ? `${r[0]} (${r[3]})` : r[0]))],
  ];
  const refDepth = Math.max(0, ...refCols.map(([, vals]) => vals.length));
  const refRows = [refCols.map(([label]) => label)];
  for (let i = 0; i < refDepth; i += 1) {
    refRows.push(refCols.map(([, vals]) => vals[i] ?? ''));
  }
  const wsRef = XLSX.utils.aoa_to_sheet(refRows);
  wsRef['!cols'] = refCols.map(() => ({ wch: 34 }));
  wsRef['!freeze'] = { xSplit: 0, ySplit: 1 };
  XLSX.utils.book_append_sheet(wb, wsRef, 'Reference Lists');

  return wb;
}

export function downloadRegionTemplate(existing = {}, filename = 'Region_Master_Import_Template.xlsx') {
  XLSX.writeFile(buildRegionTemplateWorkbook(existing), filename);
}

/* ── Reading an uploaded file ────────────────────────────────────────────── */

const sheetRows = (wb, name, def) => {
  // Sheet names are matched loosely so "police stations" or a stray space
  // still lands on the right tab.
  const actual = wb.SheetNames.find((s) => norm(s) === norm(name))
    || wb.SheetNames.find((s) => norm(s).replace(/\s/g, '') === norm(name).replace(/\s/g, ''));
  if (!actual) return [];

  const grid = XLSX.utils.sheet_to_json(wb.Sheets[actual], {
    header: 1, blankrows: false, raw: false, defval: '',
  });
  if (!grid.length) return [];

  const header = (grid[0] || []).map((h) => norm(h));
  const indexOf = def.headers.map((h) => header.indexOf(norm(h)));

  const missing = def.headers.filter((h, i) => indexOf[i] === -1);
  if (missing.length) {
    throw new Error(`Sheet "${name}" is missing the column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Download the template and use its header row.`);
  }

  const out = [];
  for (let r = 1; r < grid.length; r += 1) {
    const raw = grid[r] || [];
    const values = {};
    def.fields.forEach((f, i) => { values[f] = fmt(raw[indexOf[i]]); });
    if (def.fields.every((f) => values[f] === '')) continue;   // blank row
    out.push({ ...values, _row: r + 1 });
  }
  return out;
};

/**
 * @returns {Promise<{provinces:Array, districts:Array, electorates:Array, stations:Array}>}
 *   one object per non-blank data row, each carrying `_row` for error messages.
 */
export async function readRegionWorkbook(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false });
  if (!wb.SheetNames.length) throw new Error('The file has no readable sheet.');

  const parsed = {};
  REGION_SHEETS.forEach((def) => { parsed[def.key] = sheetRows(wb, def.sheet, def); });

  const total = REGION_SHEETS.reduce((n, def) => n + parsed[def.key].length, 0);
  if (total === 0) {
    throw new Error('No rows found. Fill in at least one of the four sheets, keeping the template’s header row.');
  }
  return parsed;
}


const keyOf = (...parts) => parts.join(' ▸ ');

export function validateRegionWorkbook(parsed, existing) {
  const ex = {
    provinces: existing?.provinces || [],
    districts: existing?.districts || [],
    electorates: existing?.electorates || [],
    stations: existing?.stations || [],
  };

  /* Existing data, indexed by the names a sheet would use. */
  const provByName = new Map();
  ex.provinces.forEach((p) => provByName.set(normProvince(p.name), p));

  const provNameOfCode = new Map(ex.provinces.map((p) => [String(p.code), p.name]));

  const distByKey = new Map();     // province ▸ district  ->  record
  ex.districts.forEach((d) => {
    const provName = provNameOfCode.get(String(d.provinceCode)) || '';
    distByKey.set(keyOf(normProvince(provName), normDistrict(d.name)), d);
  });

  const distNameOfCode = new Map(ex.districts.map((d) => [String(d.code), d.name]));

  const elecByKey = new Map();     // province ▸ district ▸ area -> record
  ex.electorates.forEach((e) => {
    const provName = provNameOfCode.get(String(e.provinceCode)) || '';
    const distName = distNameOfCode.get(String(e.districtCode)) || '';
    elecByKey.set(keyOf(normProvince(provName), normDistrict(distName), norm(e.name)), e);
  });

  const elecAreaOfCode = new Map(ex.electorates.map((e) => [String(e.code), e.name]));

  const stnByKey = new Map();      // electorate area ▸ station -> record
  ex.stations.forEach((s) => {
    const area = elecAreaOfCode.get(String(s.electorateCode)) || '';
    stnByKey.set(keyOf(norm(area), norm(s.name)), s);
  });

  /* Names this workbook is about to create, so children can reference them. */
  const pendingProv = new Set();
  const pendingDist = new Set();
  const pendingElec = new Set();

  const mark = (row, status, errors = []) => ({ ...row, status, errors });

  /* ── Provinces ───────────────────────────────────────────────────────── */
  const seenProv = new Map();
  const provinces = (parsed.provinces || []).map((row) => {
    const errors = [];
    const name = row.name;
    if (!name) errors.push('Province Name is required.');

    const key = normProvince(name);
    if (name && seenProv.has(key)) errors.push(`Repeated on this sheet (row ${seenProv.get(key)}).`);
    else if (name) seenProv.set(key, row._row);

    if (errors.length) return mark(row, 'error', errors);
    if (provByName.has(key)) return mark(row, 'exists');

    pendingProv.add(key);
    return mark(row, 'new');
  });

  const provinceKnown = (name) => provByName.has(normProvince(name)) || pendingProv.has(normProvince(name));

  /* ── Districts ───────────────────────────────────────────────────────── */
  const seenDist = new Map();
  const districts = (parsed.districts || []).map((row) => {
    const errors = [];
    if (!row.name) errors.push('District Name is required.');
    if (!row.province) errors.push('Province Name is required.');
    else if (!provinceKnown(row.province)) {
      errors.push(`Province "${row.province}" is not on the Provinces sheet and does not exist yet.`);
    }

    const key = keyOf(normProvince(row.province), normDistrict(row.name));
    if (row.name && row.province && seenDist.has(key)) {
      errors.push(`Repeated on this sheet (row ${seenDist.get(key)}).`);
    } else if (row.name && row.province) seenDist.set(key, row._row);

    if (errors.length) return mark(row, 'error', errors);
    if (distByKey.has(key)) return mark(row, 'exists');

    pendingDist.add(key);
    return mark(row, 'new');
  });

  const districtKnown = (prov, dist) => {
    const key = keyOf(normProvince(prov), normDistrict(dist));
    return distByKey.has(key) || pendingDist.has(key);
  };

  /* ── Electorates ─────────────────────────────────────────────────────── */
  const seenElec = new Map();
  const electorates = (parsed.electorates || []).map((row) => {
    const errors = [];
    if (!row.name) errors.push('Electorate Area is required.');
    if (!row.province) errors.push('Province Name is required.');
    else if (!provinceKnown(row.province)) {
      errors.push(`Province "${row.province}" is not on the Provinces sheet and does not exist yet.`);
    }
    if (!row.district) errors.push('District Name is required.');
    else if (row.province && provinceKnown(row.province) && !districtKnown(row.province, row.district)) {
      errors.push(`District "${row.district}" is not in ${row.province}.`);
    }

    const key = keyOf(normProvince(row.province), normDistrict(row.district), norm(row.name));
    if (row.name && seenElec.has(key)) errors.push(`Repeated on this sheet (row ${seenElec.get(key)}).`);
    else if (row.name) seenElec.set(key, row._row);

    if (errors.length) return mark(row, 'error', errors);
    if (elecByKey.has(key)) return mark(row, 'exists');

    pendingElec.add(keyOf(normProvince(row.province), normDistrict(row.district), norm(row.name)));
    return mark(row, 'new');
  });

  const electorateKnown = (prov, dist, area) => {
    const key = keyOf(normProvince(prov), normDistrict(dist), norm(area));
    return elecByKey.has(key) || pendingElec.has(key);
  };

  /* ── Police Stations ─────────────────────────────────────────────────── */
  const seenStn = new Map();
  const stations = (parsed.stations || []).map((row) => {
    const errors = [];
    if (!row.name) errors.push('Station Name is required.');
    if (!row.province) errors.push('Province Name is required.');
    if (!row.district) errors.push('District Name is required.');
    if (!row.electorate) errors.push('Electorate Area is required.');
    else if (row.province && row.district && !electorateKnown(row.province, row.district, row.electorate)) {
      errors.push(`Electorate "${row.electorate}" is not in ${row.district}, ${row.province}.`);
    }

    const key = keyOf(norm(row.electorate), norm(row.name));
    if (row.name && row.electorate && seenStn.has(key)) {
      errors.push(`Repeated on this sheet (row ${seenStn.get(key)}).`);
    } else if (row.name && row.electorate) seenStn.set(key, row._row);

    if (errors.length) return mark(row, 'error', errors);
    if (stnByKey.has(key)) return mark(row, 'exists');

    return mark(row, 'new');
  });

  const result = { provinces, districts, electorates, stations };

  const count = (key, status) => result[key].filter((r) => r.status === status).length;
  result.summary = REGION_SHEETS.map((def) => ({
    key: def.key,
    label: def.label,
    sheet: def.sheet,
    total: result[def.key].length,
    add: count(def.key, 'new'),
    skip: count(def.key, 'exists'),
    bad: count(def.key, 'error'),
  }));
  result.totalNew = result.summary.reduce((n, s) => n + s.add, 0);
  result.totalBad = result.summary.reduce((n, s) => n + s.bad, 0);

  return result;
}

/* ── Resolving a parent name to the code the server just allocated ───────── */

export const findProvinceCode = (provinces, name) => {
  const hit = provinces.find((p) => normProvince(p.name) === normProvince(name));
  return hit ? hit.code : '';
};

export const findDistrictCode = (districts, provinceCode, name) => {
  const hit = districts.find((d) => String(d.provinceCode) === String(provinceCode)
    && normDistrict(d.name) === normDistrict(name));
  return hit ? hit.code : '';
};

export const findElectorateCode = (electorates, provinceCode, districtCode, area) => {
  const hit = electorates.find((e) => String(e.provinceCode) === String(provinceCode)
    && String(e.districtCode) === String(districtCode)
    && norm(e.name) === norm(area));
  return hit ? hit.code : '';
};

export { fmt as rgFmt, norm as rgNormName, normProvince as rgNormProvince, normDistrict as rgNormDistrict };